import { useMemo } from 'react';
import { noop } from 'lodash';

import {
   EducationalMaterial,
   EducationalMaterialOperationRegisterCommand,
   EducationalMaterialOperationType,
   EducationalMaterialQuery,
} from '@dreryk/egabinet-knowledge-base-frontend-api';
import { VisitPatient } from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   JustifyContent,
   Padding,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot, TextPattern } from '@dreryk/watermelon';
import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';

import { VisitId } from '~libs.shared.visit';
import { useGetEducationalMaterialTags } from '~libs.desktop.shared.educational-material';

import { educationalMaterialsTranslationKeys as t } from '../translations';

import { OptionsCell } from './cells/OptionsCell';

const DEFAULT_ORDERINGS: Ordering<EducationalMaterial>[] = [
   {
      descending: true,
      property: 'creationDate',
   },
   {
      descending: false,
      property: 'name',
   },
];

export const useEducationalMaterialVisitOperationRegisterCommand = (
   options: EducationalMaterial[]
): UseCqrsCommandResult<void, EducationalMaterialOperationType> => {
   const educationalMaterialIds = useMemo<number[]>(() => {
      return options.map((material) => material.id);
   }, [options]);
   return useCqrsCommand(
      (operationType) => {
         const command = new EducationalMaterialOperationRegisterCommand();
         command.educationalMaterialIds = educationalMaterialIds;
         command.operationType = operationType;
         command.operationOrigin = 'VISIT';
         return command;
      },
      {
         onUnexpectedError: noop,
         background: true,
      }
   );
};

type UseEducationalMaterialsDataConfigurationOptions = {
   searchText: string | null;
};

export const useEducationalMaterialsDataConfiguration = (
   options: UseEducationalMaterialsDataConfigurationOptions
): LazyLoadedDataConfigurationByCqrsQuery<EducationalMaterial> => {
   const { tags } = useGetEducationalMaterialTags();

   return useLazyLoadedDataConfigurationByCqrsQuery<EducationalMaterial>(() => {
      const query = new EducationalMaterialQuery();
      query.categories = Slot.of(['FOR_PATIENT']);

      if (tags.length) {
         query.tags = Slot.of(tags);
      }

      if (options.searchText) {
         query.nameOrAuthor = TextPattern.anywhereIgnoreCase(options.searchText);
      }
      return query;
   }, DEFAULT_ORDERINGS);
};

type UseEducationalMaterialsColumnsOptions = {
   patientEmail?: VisitPatient['emailAddress'];
   visitId: VisitId;
};

export const useEducationalMaterialsColumns = (
   options: UseEducationalMaterialsColumnsOptions
): TableColumnConfiguration<EducationalMaterial>[] => {
   return useMemo((): TableColumnConfiguration<EducationalMaterial>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         name: '100%',
         creationDate: '142px',
         authors: '267px',
         options: '104px',
      });

      return [
         {
            name: 'name',
            label: t.form.table.headers.name,
            width: columnsWidth.name,
            accessor: 'name',
         },
         {
            name: 'creationDate',
            label: t.form.table.headers.creationDate,
            width: columnsWidth.creationDate,
            accessor: 'creationDate',
            formatter: dateFormatter,
         },
         {
            name: 'authors',
            label: t.form.table.headers.authors,
            width: columnsWidth.authors,
            accessor: noop,
            renderer: ({ bean }: ValueRendererProps<EducationalMaterial>) => <span>{bean.authors.join(', ')}</span>,
         },
         {
            width: columnsWidth.options,
            justifyContent: JustifyContent.END,
            accessor: noop,
            name: 'options',
            padding: Padding.NO_RIGHT,
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<EducationalMaterial>) => (
               <OptionsCell
                  contentUrl={bean.contentUrl}
                  visitId={options.visitId}
                  patientEmail={options.patientEmail}
                  educationalMaterials={[bean]}
               />
            ),
         },
      ];
   }, [options.patientEmail, options.visitId]);
};
